import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormatedSavedFilter, NamedFilter, SavedFilter } from '@teammood/filter';
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { FilterDialogService } from 'app/frontend/lib/filter/filter-dialog/filter-dialog.service';
import { FiltersService } from 'app/frontend/lib/filter/filters.service';
import { takeUntil, tap } from 'rxjs';
import { LoggerService } from '@teammood/shared/util/logger';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'filters',
    templateUrl: './filters.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FiltersComponent implements OnInit {

    customFilters: FormatedSavedFilter[];
    namedFilters: FormatedSavedFilter[];
    filterModal: {
        namedFilter?: NamedFilter;
        mode?: string; 
        show: boolean; 
    };
    namedFilter: SavedFilter;

    constructor(
        private logService: LoggerService, 
        private translateService: TranslateService,
        private filtersService: FiltersService, 
        private snackBarService: SnackBarService,
        private filterDialogService: FilterDialogService) {
    }

    async ngOnInit() {
        await this.load();
    }
    async load() {
        try {
            const filters = await this.filtersService.filters();
            this.namedFilters = this.filtersService.formatFilters(filters, this.create, this.update);
            this.customFilters = this.filtersService.getCustomFilters(this.namedFilters);
        } catch (error) {
            this.logService.error("filters loading error", error);
            if (error.status === 403) {
                alert('Well tried, but you are not authorized or you have to upgrade to the Premium plan');
            }
        }
    }
    async create(filterDTO: SavedFilter) {
        try {
            const result = await this.filtersService.createFilter(filterDTO);
            this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update);
            this.customFilters = this.filtersService.getCustomFilters(this.namedFilters);
            this.snackBarService.success(this.translateService.instant('filters_setting.success_create'));
        } catch (error) {
            this.snackBarService.error(JSON.stringify(error.error));
            this.logService.error('Filter creation error', error);
        }
    }

    async update(filterDTO: SavedFilter) {
        try {
            const result = await this.filtersService.updateFilter(filterDTO);
            this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update);
            this.customFilters = this.filtersService.getCustomFilters(this.namedFilters);
            this.snackBarService.success(this.translateService.instant('filters_setting.success_update'));
        } catch (error) {
            this.snackBarService.error(JSON.stringify(error.error));
            this.logService.error('Filter update error', error);
        }
    }

    addFilter (){
        const namedFilter: NamedFilter = {
            default: false,
            name: undefined,
            decodedFilter: {
                predicates: []
            }
        };
        this.onOpenFilterDialog(namedFilter, 'create');
    }
    
    async onOpenFilterDialog(filter, mode) {
        const dialogRef = await this.filterDialogService.openFilterDialog(filter, mode);

        dialogRef.beforeClosed().pipe(
            tap(data => {
                if (data) this.onCreateOrUpdateFilter(data);
            }),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    onCreateOrUpdateFilter (data: { filter: SavedFilter, mode: string }) {
        if (data.mode === 'update' && data.filter.id) {
            this.update(data.filter);
        } else if (data.mode === 'create') {
            this.create(data.filter);
        }
    }

    updateFilter (filter: NamedFilter){
        this.onOpenFilterDialog(filter, 'update');
    }

    async duplicate (filterId: string){
        const result = await this.filtersService.duplicateFilter(filterId);
        this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update);
        this.customFilters = this.filtersService.getCustomFilters(this.namedFilters);
    }

    async setAsDefault (filterId: string){
        const result = await this.filtersService.setAsDefault(filterId);
        this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update);
        this.customFilters = this.filtersService.getCustomFilters(this.namedFilters);
    }

    async remove (filterId: string){
        const result = await this.filtersService.removeFilter(filterId);
        this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update);
        this.customFilters = this.filtersService.getCustomFilters(this.namedFilters);
    }

}
